/**
 * The muzzle flash (owner, Oct 10: the "explosion from gun barrel blends in with terrain a little too well"). Was a
 * small pale sphere for a twentieth of a second, the colour of dry grass. Now three parts at the muzzle:
 *   - a white-hot core and a tongue of flame along the shot, both drawn additively (they brighten whatever is
 *     behind them — sky, grass, rock — so they never take its colour), for a few hundredths of a second;
 *   - a puff of grey smoke that swells and drifts up over half a second, so a shot leaves a mark even when the
 *     flash itself fell between frames.
 * Sized from the person; the geometries are shared across shots (only the materials, which fade, are per shot — 69.98:
 * taken from rendering/flashMaterials.ts and given back, so their programs are kept).
 * Presentation only.
 */
import * as THREE from 'three';
import type { Vec3 } from '../types';
import { flashMaterial } from './flashMaterials';

export interface FlashPart { mesh: THREE.Mesh; life: number; grow: number; peak: number; rise?: THREE.Vector3; shared: true }

/** The parts' numbers: sizes in people, lives in seconds, the smoke's rise in people a second. */
export const MUZZLE = { core: 0.09, coreLife: 0.06, flameLength: 0.42, flameWidth: 0.07, flameLife: 0.07, smoke: 0.07, smokeGrow: 3.2, smokeLife: 0.6, smokeRise: 0.6, smokeAhead: 0.5 };

let sphere: THREE.SphereGeometry | null = null, cone: THREE.ConeGeometry | null = null;
const shared = () => {
  sphere ??= new THREE.SphereGeometry(1, 8, 6);
  if (!cone) { cone = new THREE.ConeGeometry(1, 1, 8, 1, true); cone.translate(0, -0.5, 0); cone.rotateX(Math.PI); }   // its base at the origin, its tip along +y
  return { sphere, cone: cone! };
};
const _y = new THREE.Vector3(0, 1, 0);

/** The flash at the muzzle `at` for a shot along `dir`, for a person `person` tall, with the ground's `up`. */
export function muzzleFlashParts(at: Vec3, dir: Vec3, up: Vec3, person: number, drift?: THREE.Vector3): FlashPart[] {
  const g = shared(), M = MUZZLE;
  const d = new THREE.Vector3(dir.x, dir.y, dir.z).normalize();
  const add = (color: string, opacity: number) => flashMaterial(color, opacity, 'additive');
  const core = new THREE.Mesh(g.sphere, add('#fff6d8', 1));
  core.position.set(at.x, at.y, at.z); core.scale.setScalar(M.core * person);
  const flame = new THREE.Mesh(g.cone, add('#ffa040', 1));
  flame.position.set(at.x, at.y, at.z); flame.quaternion.setFromUnitVectors(_y, d);
  flame.scale.set(M.flameWidth * person, M.flameLength * person, M.flameWidth * person);
  const smoke = new THREE.Mesh(g.sphere, flashMaterial('#b8b4ac', 0.45));
  smoke.position.set(at.x, at.y, at.z); smoke.scale.setScalar(M.smoke * person);
  const rise = new THREE.Vector3(up.x, up.y, up.z).normalize().multiplyScalar(M.smokeRise * person).addScaledVector(d, M.smokeAhead * person);
  if (drift) rise.add(drift);   // 67.36: the puff leans downwind (windView smokeDrift; world units a second)
  return [
    { mesh: core, life: M.coreLife, grow: 1.4, peak: 1, shared: true },
    { mesh: flame, life: M.flameLife, grow: 1.3, peak: 1, shared: true },
    { mesh: smoke, life: M.smokeLife, grow: M.smokeGrow, peak: 0.45, rise, shared: true },
  ];
}
